import React, {Fragment, useCallback, useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {toast} from "react-toastify";
import Select from "react-select";
import {Badge, Card, CardBody, Col, Container, Form, FormGroup, Input, Row} from "reactstrap";
import Breadcrumbs from "../../layout/breadcrumb";
import CommonDataTable from "../../share-components/table/commonDataTable";
import {
    deleteWorkflowApi,
    duplicateWorkflowApi,
    getCreatedByUsersApi,
    getWorkflowsApi,
    getWorkflowSummaryApi,
    pauseWorkflowApi,
    resumeWorkflowApi,
    runAllWorkflowsApi,
    runWorkflowApi
} from "../../services/tagsAutomationService";
import {BASE, ConditionLine, errorMessage, number, RequirePermission, TagBadge, TagList, useCan} from "./common";
import WorkflowDialog from "./workflowDialog";
import "./tagsAutomation.scss";

const STATUSES = [
    {value: 'active', label: 'Active'},
    {value: 'paused', label: 'Paused'},
];

const TRIGGERS = [
    {value: 'created', label: 'Product created'},
    {value: 'updated', label: 'Product updated or sold'},
    {value: 'nightly', label: 'Nightly'},
];

const selectStyles = {
    control: (base) => ({...base, minHeight: 50, border: "none"}),
};

const dataMenu = [
    {type: 1, text: 'Tags Automation', link: '/tags-automation/workflows'},
    {type: 0, text: 'Workflows'},
];

/**
 * GOD-TASK-197 Tags Automation › Workflows (prototype screen 1).
 */
const WorkflowList = () => {
    const can = useCan();
    const [filters, setFilters] = useState({search: '', status: '', trigger: '', created_by: ''});
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [resetPage, setResetPage] = useState(false);
    const [list, setList] = useState({data: [], total: 0});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);
    const [summary, setSummary] = useState(null);
    const [dialog, setDialog] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        getWorkflowsApi({...filters, page, page_size: pageSize})
            .then(res => {
                setList(res);
                setError(null);
            })
            .catch(e => setError(errorMessage(e)))
            .finally(() => setLoading(false));
    }, [filters, page, pageSize]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        getCreatedByUsersApi().then(setUsers).catch(() => {});
        getWorkflowSummaryApi().then(setSummary).catch(() => {});
    }, []);

    const toFirstPage = () => {
        setPage(1);
        setResetPage(r => !r);
    };

    // Search waits until typing pauses.
    useEffect(() => {
        const t = setTimeout(() => {
            setFilters(f => (f.search === search ? f : {...f, search}));
            toFirstPage();
        }, 400);
        return () => clearTimeout(t);
    }, [search]);

    const setFilter = (key, option) => {
        setFilters(f => ({...f, [key]: option ? option.value : ''}));
        toFirstPage();
    };

    const act = (promise, message) => {
        setBusy(true);
        return promise
            .then(res => {
                toast.success(message(res), {position: toast.POSITION.TOP_RIGHT});
                load();
                getWorkflowSummaryApi().then(setSummary).catch(() => {});
            })
            .catch(e => toast.error(errorMessage(e), {position: toast.POSITION.TOP_RIGHT}))
            .finally(() => {
                setBusy(false);
                setDialog(null);
            });
    };

    const queued = (res) => res.products_queued ? ` ${number(res.products_queued)} products queued for re-sync.` : '';

    const confirmDialog = (action) => {
        const w = dialog.workflow;
        if (dialog.mode === 'delete') {
            act(deleteWorkflowApi(w.id, action), res => `Workflow ${w.name} deleted.${queued(res)}`);
        } else {
            act(pauseWorkflowApi(w.id, action), res => `Workflow ${w.name} paused.${queued(res)}`);
        }
    };

    const runStarted = () => 'Run started. Progress and results appear in the Activity Log.';

    const columns = [
        {name: 'ID', selector: row => `#${row.id}`, width: '80px'},
        {
            name: 'Workflow',
            selector: row => can('Update Workflow')
                ? <Link className="f-w-500" to={`${BASE}/workflows/${row.id}/edit`}>{row.name}</Link>
                : <span className="f-w-500">{row.name}</span>,
            grow: 1,
            minWidth: '220px',
            wrap: true,
        },
        {
            name: 'Status',
            selector: row => <Badge color={row.status === 'paused' ? 'warning' : 'success'}>{row.status === 'paused' ? 'Paused' : 'Active'}</Badge>,
            // The theme gives table badges min-width 90px plus padding; the cell needs room for it.
            width: '140px',
        },
        {name: 'Triggers', selector: row => <span className="f-12 text-muted">{row.triggers_label}</span>, grow: 1, wrap: true},
        {name: 'Action', selector: row => <TagBadge tag={row.action} kind={row.action_type === 'remove' ? 'remove' : 'add'}/>, grow: 1, wrap: true},
        {name: 'Last run', selector: row => <span className="f-12 text-muted">{row.last_run}</span>, width: '160px', wrap: true},
        {name: 'Products tagged', selector: row => number(row.products_tagged), width: '170px', right: true},
        {
            name: '',
            selector: row => (
                <div className="d-flex" style={{gap: 6}}>
                    <Link className="btn btn-xs btn-outline-primary" to={`${BASE}/workflows/${row.id}/test`}>Test</Link>
                    {can('Update Workflow') && (row.status === 'paused'
                        ? <button type="button" className="btn btn-xs btn-outline-success" disabled={busy}
                                  onClick={() => act(resumeWorkflowApi(row.id), res => `Workflow ${row.name} resumed.${queued(res)}`)}>Resume</button>
                        : <button type="button" className="btn btn-xs btn-outline-danger" disabled={busy}
                                  onClick={() => setDialog({mode: 'pause', workflow: row})}>Pause</button>)}
                </div>
            ),
            width: '170px',
        },
    ];

    const Summary = ({data: w}) => {
        const kind = w.action_type === 'remove' ? 'remove' : 'add';
        const tags = w.action_type === 'remove' ? w.remove_tags : w.add_tags;
        const patterns = w.remove_patterns || [];
        return (
            <div className="p-3 ta-soft d-flex flex-column" style={{gap: 10}}>
                <div className="f-12 text-muted">Process products</div>
                {w.conditions.length === 0 && <div className="f-12">Every product (no conditions)</div>}
                {w.conditions.map((c, i) => <ConditionLine key={i} c={c}/>)}
                <div>Runs <b>{w.runs_text}</b></div>
                <div className="d-flex align-items-center flex-wrap" style={{gap: 6}}>
                    {tags.length > 0 && <>
                        <span>{w.action_type === 'remove' ? 'Remove tags:' : 'Add tags:'}</span>
                        <TagList tags={tags} kind={kind}/>
                    </>}
                    {w.action_type === 'both' && w.remove_tags.length > 0 && <>
                        <span>Remove tags:</span>
                        <TagList tags={w.remove_tags} kind="remove"/>
                    </>}
                    {patterns.length > 0 && <>
                        <span>Remove tags matching:</span>
                        <TagList tags={patterns} kind="remove"/>
                    </>}
                    <span className="f-12 text-muted font-italic">{w.action_note}</span>
                </div>
                <div className="d-flex mt-1" style={{gap: 8}}>
                    {can('Update Workflow') && <Link className="btn btn-sm btn-outline-primary" to={`${BASE}/workflows/${w.id}/edit`}>Edit</Link>}
                    {can('Create Workflow') && <button className="btn btn-sm btn-outline-primary" type="button" disabled={busy}
                            onClick={() => act(duplicateWorkflowApi(w.id), res => `Created ${res.name}.`)}>Duplicate</button>}
                    {can('Update Workflow') && <button className="btn btn-sm btn-outline-primary" type="button" disabled={busy}
                            onClick={() => act(runWorkflowApi(w.id), runStarted)}>Run now</button>}
                    {can('Delete Workflow') && <button className="btn btn-sm btn-outline-danger" type="button" disabled={busy}
                            onClick={() => setDialog({mode: 'delete', workflow: w})}>Delete</button>}
                </div>
            </div>
        );
    };

    const last = summary?.last_nightly;
    const optionFor = (options, value) => options.find(o => String(o.value) === String(value)) || null;
    const userOptions = users.map(u => ({value: u.id, label: u.name}));

    return (
        <Fragment>
            <Breadcrumbs title="Tags Automation" data={dataMenu}/>
            <Container fluid={true} className="product-wrapper">
                <div className="product-grid">
                    {(can('Activity Log') || can('Update Workflow') || can('Create Workflow')) && <div className="feature-products">
                        <Card>
                            <Row className="align-items-center">
                                <Col sm="12">
                                    <div className="d-flex justify-content-end flex-wrap p-3" style={{gap: 8}}>
                                        {can('Activity Log') && <Link className="btn btn-sm btn-outline-primary" to={`${BASE}/activity-log`}><i className="fa fa-list-alt"/> Activity log</Link>}
                                        {can('Update Workflow') && <button className="btn btn-sm btn-outline-primary" type="button" disabled={busy} onClick={() => act(runAllWorkflowsApi(), runStarted)}>
                                            <i className="fa fa-refresh"/> Run all now
                                        </button>}
                                        {can('Create Workflow') && <Link className="btn btn-sm btn-primary" to={`${BASE}/workflows/create`}><i className="fa fa-plus"/> Create Workflow</Link>}
                                    </div>
                                </Col>
                            </Row>
                        </Card>
                    </div>}

                    {error && <div className="alert alert-danger">Could not load the workflows: {error}</div>}

                    <Row>
                        <Col md={3}>
                            <Form onSubmit={e => e.preventDefault()}>
                                <FormGroup className="m-0">
                                    <Input bsSize="lg" type="text" placeholder="Search workflow name or tag" value={search} onChange={e => setSearch(e.target.value)}/>
                                </FormGroup>
                            </Form>
                        </Col>
                        <Col md={3}>
                            <Select placeholder="Status" className="select2-filter" isClearable styles={selectStyles}
                                    options={STATUSES} value={optionFor(STATUSES, filters.status)}
                                    onChange={o => setFilter('status', o)} components={{IndicatorSeparator: () => null}}/>
                        </Col>
                        <Col md={3}>
                            <Select placeholder="Trigger" className="select2-filter" isClearable styles={selectStyles}
                                    options={TRIGGERS} value={optionFor(TRIGGERS, filters.trigger)}
                                    onChange={o => setFilter('trigger', o)} components={{IndicatorSeparator: () => null}}/>
                        </Col>
                        <Col md={3}>
                            <Select placeholder="Created by" className="select2-filter" isClearable styles={selectStyles}
                                    options={userOptions} value={optionFor(userOptions, filters.created_by)}
                                    onChange={o => setFilter('created_by', o)} components={{IndicatorSeparator: () => null}}/>
                        </Col>
                    </Row>

                    <Row>
                        <Col className="mt-3" sm="12">
                            <Card>
                                <CardBody>
                                    <div className="table-responsive product-table">
                                        <CommonDataTable
                                            headerColumns={columns}
                                            gridData={list.data}
                                            noAction
                                            progressPending={loading}
                                            paginationServer
                                            paginationTotalRows={list.total}
                                            paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                            paginationPerPage={pageSize}
                                            paginationDefaultPage={1}
                                            paginationResetDefaultPage={resetPage}
                                            onChangePage={setPage}
                                            onChangeRowsPerPage={size => {
                                                setPageSize(size);
                                                toFirstPage();
                                            }}
                                            expandableRows
                                            expandableRowsComponent={Summary}
                                            noDataComponent={<div className="p-4 text-center">No workflows match these filters.</div>}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {summary && (
                        <Card>
                            <CardBody>
                                <Row>
                                    <Col md={5}>
                                        <div className="f-12 text-muted">Last nightly run</div>
                                        <div className="f-w-500">
                                            {last ? <>{last.date} · {number(last.checked)} products checked · {number(last.changed)} changed · <span className={last.failed ? 'text-danger' : ''}>{number(last.failed)} failed</span></>
                                                : 'No nightly run yet'}
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="f-12 text-muted">Next nightly run</div>
                                        <div className="f-w-500">{summary.next_nightly}</div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="f-12 text-muted">Failed writes, retried first next run</div>
                                        <div className={`f-w-500 ${summary.failed_writes ? 'text-danger' : ''}`}>
                                            {number(summary.failed_writes)}{can('Activity Log') && <> · <Link to={`${BASE}/activity-log?result=failed`}>view</Link></>}
                                        </div>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    )}
                </div>
            </Container>

            {dialog && <WorkflowDialog workflow={dialog.workflow} mode={dialog.mode} busy={busy}
                                       onCancel={() => setDialog(null)} onConfirm={confirmDialog}/>}
        </Fragment>
    );
};

const WorkflowListPage = () => <RequirePermission name="Workflows"><WorkflowList/></RequirePermission>;

export default WorkflowListPage;
